import { useRef } from 'react';
import {
  DateRangePickerStateOptions,
  useDateRangePickerState,
} from 'react-stately';
import { useDateRangePicker, useLocale } from 'react-aria';
import {
  CalendarIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { DateField, TimeField } from '../Calendar/components/DateField';
import { FieldButton } from '../Calendar/components/Button';
import { RangeCalendar } from './RangeCalendar';
import { DateValue } from '@internationalized/date';
import { Modal } from '../Modal';

export interface DateRangePickerProps extends DateRangePickerStateOptions {
  /** Label of the input
   * For accessibility only
   */
  label: string;
  /** Label of the start time
   * by default it is Start Time
   */
  startLabel?: string;
  /** Label of the end time
   * by default it is End Time
   */
  endLabel?: string;
  /** The minimum allowed date that a user may select.
   * if left empty it will be the `now()`
   */
  minValue?: DateValue;
  /**
   * The maximum allowed date that a user may select.
   * if left empty it will be 31/12/9999
   */
  maxValue?: DateValue;
  /**
   * The default Date range that the component will render with
   * it's preferable to not leave it empty, otherwise it will show the placeholder
   * which is the locale format e.g. (dd/mm/yyy ...)
   */
  defaultValue?: { start: DateValue; end: DateValue };
  /** Handler that is called when the value changes. */
  onChange?: (value: { start: DateValue; end: DateValue }) => void;
  className?: string;
}

export function DateRangePicker({
  startLabel = 'Start Time',
  endLabel = 'End Time',
  granularity = 'minute',
  hideTimeZone = true,
  className = '',
  ...props
}: DateRangePickerProps) {
  const state = useDateRangePickerState({
    ...props,
    shouldCloseOnSelect: false,
  });
  const { locale } = useLocale();
  const ref = useRef(null);
  const {
    groupProps,
    startFieldProps,
    endFieldProps,
    buttonProps,
    calendarProps,
  } = useDateRangePicker({ ...props, granularity, hideTimeZone }, state, ref);
  return (
    <div className="relative inline-flex flex-col justify-center items-center text-left @container w-full">
      <div {...groupProps} className="flex group w-full" title={props.label}>
        <div
          className={`flex flex-col @[540px]:flex-row @[540px]:justify-start @[540px]:items-start @[540px]:pl-4 w-full justify-center items-center bg-white border pl-1 pr-8 py-1.5 ${
            state.validationState === 'invalid' || props.errorMessage
              ? 'border-red-500 group-focus-within:border-red-500 group-hover:border-red-500  group-focus-within:group-hover:border-red-500'
              : 'border-gray-300 group-hover:border-gray-400 group-focus-within:border-primary-600 group-focus-within:group-hover:border-primary-600'
          }   transition-colors rounded-l-md  ${className}  relative`}
        >
          <DateField {...startFieldProps} />
          <span aria-hidden="true" className="px-2 hidden @[540px]:inline">
            –
          </span>
          <DateField {...endFieldProps} />
          {(state.validationState === 'invalid' || props.errorMessage) && (
            <ExclamationCircleIcon className="w-6 h-6 text-red-500 absolute right-1 inset-y-0 my-auto" />
          )}
        </div>
        <FieldButton
          {...buttonProps}
          isPressed={state.isOpen}
          isError={
            state.validationState === 'invalid' || Boolean(props.errorMessage)
          }
        >
          <CalendarIcon
            ref={ref}
            className={`w-5 h-5 ${
              state.validationState === 'invalid' || props.errorMessage
                ? 'text-red-500 group-focus-within:text-red-500'
                : 'text-gray-700 group-focus-within:text-primary-700'
            }  `}
          />
        </FieldButton>
      </div>
      {/* {state.isOpen && (
        <Popover triggerRef={ref} state={state} placement="bottom">
          <div className=" ">
            <Dialog {...dialogProps}>
              <RangeCalendar {...calendarProps} />
              {granularity === 'minute' && (
                <>
                  {' '}
                  <TimeField
                    locale={locale}
                    label={startLabel}
                    value={state.timeRange?.start || null}
                    onChange={(v) => state.setTime('start', v)}
                  />
                  <TimeField
                    locale={locale}
                    label={endLabel}
                    value={state.timeRange?.end || null}
                    onChange={(v) => state.setTime('end', v)}
                  />
                </>
              )}
            </Dialog>
          </div>
        </Popover>
      )} */}
      <Modal
        className="!sm:w-fit !w-fit"
        show={state.isOpen}
        onClose={() => state.close()}
      >
        <div
          className={`${
            granularity === 'minute' ? 'min-h-[450px]' : 'min-h-[350px]'
          } flex flex-col justify-between w-fit`}
        >
          <RangeCalendar {...calendarProps} />
          {granularity === 'minute' && (
            <div>
              {' '}
              <TimeField
                locale={locale}
                label={startLabel}
                value={state.timeRange?.start || null}
                onChange={(v) => state.setTime('start', v)}
              />
              <TimeField
                locale={locale}
                label={endLabel}
                value={state.timeRange?.end || null}
                onChange={(v) => state.setTime('end', v)}
              />
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
